import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';

import { memberClient } from '../../config/Apollo';
import { colors } from '../../themes/colors';
import { textInputStyle } from '../../themes/input';
import { ReactComponent as IconClose } from '../../assets/svgs/icon-close.svg';
import { delay } from '../../helpers/delay';
import { CoachAssignment } from '../../models/Common';
import { CoachingVisit, FormattedCoachingVisit } from '../../models/Sessions';
import { BannerState, InfoBanner } from '../Errors/InfoBanner';
import InvoicingForm from '../Invoicing/InvoicingForm';

const styles = (theme: Theme) =>
  createStyles({
    ...textInputStyle(theme),
    title: {
      fontFamily: 'Avenir-Black',
      fontSize: 28,
      color: colors.black4,
    },
    dialogTitle: {
      padding: '32px 40px 0px 40px',
    },
    dialogContent: {
      display: 'flex',
      overflowY: 'initial',
      padding: 0,
      flex: 'none',
      marginBottom: '5px',
    },
    visitRow: {
      margin: '10px 40px',
    },
    formLabelBlue: {
      fontFamily: 'Avenir-Black',
      fontSize: 16,
      color: colors.blue1,
    },
    secondaryLabel: {
      fontFamily: 'Avenir-Light',
      fontSize: 16,
      color: colors.black5,
      fontWeight: 800,
    },
    dialogPaper: {
      overflowY: 'scroll',
    },
    closeButton: {
      position: 'absolute',
      right: 0,
    },
  });

type Props = WithStyles & {
  coachAssignment: CoachAssignment;
  onCancel: () => void;
  onSuccess: () => void;
  onUpdateVisit?: () => void;
  showSuccessModal?: boolean;
  visit?: FormattedCoachingVisit | CoachingVisit;
};

enum VisibilityState {
  Hidden,
  Visible,
  Closed,
}

const ReportVisit: React.FC<Props> = ({
  classes,
  coachAssignment,
  onCancel,
  onSuccess,
  onUpdateVisit,
  showSuccessModal,
  visit,
}) => {
  const [modalVisible, setModalVisible] = useState(true);
  const [successModalState, setSuccessModalState] = useState(VisibilityState.Hidden);
  const [autoCloseTimeout, setAutoCloseTimeout] = useState();

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    return () => {
      if (autoCloseTimeout) {
        clearTimeout(autoCloseTimeout);
      }
    };
  }, [autoCloseTimeout]);

  const setError = (message: string) => {
    setShowError(true);
    setErrorMessage(message);
  };

  const hideSuccessModal = async () => {
    setSuccessModalState(VisibilityState.Closed);

    await delay(700);

    onSuccess();
  };

  const handleSuccess = async () => {
    if (!showSuccessModal) {
      return onSuccess();
    }

    setModalVisible(false);
    setSuccessModalState(VisibilityState.Visible);

    setAutoCloseTimeout(setTimeout(hideSuccessModal, 3000) as any);
  };

  return (
    <>
      {successModalState === VisibilityState.Hidden ? null : (
        <div
          className="fixed inset-0	flex bg-black	bg-opacity-50 z-20 justify-center	items-center"
          onClick={hideSuccessModal}
        >
          <div
            className={`animate__animated bg-white z-30 flex flex-col rounded-lg ${
              successModalState === VisibilityState.Visible && 'animate__bounceInDown'
            } ${successModalState === VisibilityState.Closed && 'animate__bounceOutDown'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <button
                onClick={hideSuccessModal}
                className="bg-gray-300 hover:bg-gray-200 cursor-pointer rounded-full h-8 w-8 absolute top-4 right-4 flex justify-center items-center"
              >
                <IconClose className="w-2.25 h-2.25 text-gray-600 fill-current stroke-current" />
              </button>
              <img
                src={require('../../assets/images/illu-confirmation.svg')}
                className="pl-10 pt-10"
                alt="confirmation_image"
              />
            </div>
            <div className="flex flex-col items-center">
              <h4 className="text-gray-900 text-2xl font-extrabold mt-8 mb-4">
                Invoice successfully submitted{' '}
                <span aria-label="party-popper" className="ml-2" role="img">
                  🎉
                </span>
              </h4>

              <p className="font-normal text-gray-600">Questions or feedback? Please email:</p>
              <a
                className="text-blue-200 font-extrabold pointer-events-auto mb-14"
                href="mailto:provider@joinmodernhealth.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                provider@joinmodernhealth.com.
              </a>
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={modalVisible}
        onClose={onCancel}
        aria-labelledby="form-dialog-title"
        fullScreen
        classes={{ paper: classes.dialogPaper }}
      >
        <IconButton aria-label="Close" onClick={onCancel} className={classes.closeButton}>
          <CloseIcon />
        </IconButton>

        <DialogTitle id="form-dialog-title" className={classes.dialogTitle} disableTypography>
          {showError && <InfoBanner message={errorMessage} bannerState={BannerState.Error} />}
          <Typography className={classes.title}>Invoice Submission</Typography>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <div className={classes.visitRow}>
            <Typography className={classes.secondaryLabel}>
              We collect this information to understand how to improve our services. We keep your
              information confidential, and we don't use this as part of any evaluation. If you have
              questions or need additional help, email:{' '}
              <label className={classes.formLabelBlue}>
                <a
                  href="mailto:provider@joinmodernhealth.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  provider@joinmodernhealth.com
                </a>
              </label>
            </Typography>
          </div>
        </DialogContent>

        <InvoicingForm
          canSelectReportedCoachingVisits={!visit}
          hideSuccessSnackbar={showSuccessModal}
          inModal
          isProviderInvoicingOnly={false}
          memberClient={memberClient}
          onCancel={onCancel}
          onSuccess={handleSuccess}
          onUpdateVisit={onUpdateVisit}
          setError={setError}
          specificCoachAssignment={coachAssignment}
          specificVisit={visit}
        />
      </Dialog>
    </>
  );
};

export default withStyles(styles)(ReportVisit);
